import * as Yup from 'yup';
import { FieldData } from '@/components/Form/types';
import { Select, TextField, InteracWordmark } from 'vopay';
import { useTranslation, Trans } from 'react-i18next';

type ETransferMethod = 'Email' | 'PhoneNumber';

export type FormValues = {
  eTransferMethod: ETransferMethod;
  email: string;
  phone: string;
  question: string;
  answer: string;
};

export const initialValues: FormValues = {
  eTransferMethod: 'Email',
  email: '',
  phone: '',
  question: '',
  answer: '',
};

export default function useFormConfig(): FieldData<FormValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.ProvidePaymentMethod' });

  return [
    {
      label: () => (
        <Trans
          ns="translations"
          i18nKey="Customers.PaymentMethodsTab.AddInteracModal.eTransferMethod"
          components={{
            InteracWordmark: <InteracWordmark />,
          }}
        />
      ),
      fieldName: 'eTransferMethod',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: () => {
            ctx.setFieldValue('email', '', false);
            ctx.setFieldValue('phone', '', false);
            ctx.setFieldTouched('email', false);
            ctx.setFieldTouched('phone', false);
          },
        });

        return (
          <Select
            version="2"
            options={[
              {
                value: 'Email',
                label: t('email', { defaultValue: 'Email Address' }),
              },
              {
                value: 'PhoneNumber',
                label: t('phone', { defaultValue: 'Phone Number' }),
              },
            ]}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('email', { defaultValue: 'Email Address' }),
      fieldName: 'email',
      getInputState: (ctx) => (ctx.values.eTransferMethod === 'Email' ? 'required' : 'optional'),
      showField: (values) => values.eTransferMethod === 'Email',
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<string>({})} />;
      },
      validate: () =>
        Yup.string()
          .email(t('Validations.invalidEmail', { defaultValue: 'Invalid email' }))
          .when('eTransferMethod', {
            is: 'Email',
            then: Yup.string().label(t('email')).required(),
          }),
    },
    {
      label: () => t('phone', { defaultValue: 'Phone Number' }),
      fieldName: 'phone',
      getInputState: (ctx) => (ctx.values.eTransferMethod === 'PhoneNumber' ? 'required' : 'optional'),
      showField: (values) => values.eTransferMethod === 'PhoneNumber',
      renderField: ({ getFormikData }) => {
        return <TextField preset="tel" variant="outlineShadow" {...getFormikData<string>({})} />;
      },
      validate: () =>
        Yup.string().when('eTransferMethod', {
          is: 'PhoneNumber',
          then: Yup.string().label('phone').required(),
        }),
    },
    {
      label: () => t('securityQuestion', { defaultValue: 'Security Question' }),
      fieldName: 'question',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<string>({})} />;
      },
    },
    {
      label: () => t('securityAnswer', { defaultValue: 'Security Answer' }),
      fieldName: 'answer',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return <TextField variant="outlineShadow" {...getFormikData<string>({})} />;
      },
    },
  ];
}
